import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import Notification from '../common/Notification';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  margin: 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
`;

const EmptyRow = styled.div`
  color: var(--text-secondary);
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1rem;
`;

const RequestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

const RequestCard = styled.article`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-left: 4px solid #d97706;
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  padding: 1rem 1.25rem;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const RequestMeta = styled.div`
  min-width: 0;
`;

const RequestTitle = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 800;
  margin-bottom: 0.35rem;
`;

const ChangePill = styled.span`
  border-radius: 6px;
  background: rgba(217, 119, 6, 0.1);
  color: #a85514;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 0.25rem 0.5rem;
  text-transform: uppercase;
`;

const RequestDetail = styled.div`
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.45;
`;

const Reason = styled.div`
  color: var(--text-muted);
  font-size: 0.88rem;
  font-style: italic;
  margin-top: 0.5rem;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const ApproveButton = styled.button`
  border: 1px solid var(--success-dark);
  background: transparent;
  color: var(--success-dark);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  padding: 0.55rem 1rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DenyButton = styled.button`
  border: 1px solid var(--error-color);
  background: transparent;
  color: var(--error-color);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  padding: 0.55rem 1rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

function AdminAttendanceCorrections() {
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [reviewingCorrectionId, setReviewingCorrectionId] = useState(null);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });

  const fetchCorrectionRequests = useCallback(async () => {
    try {
      const res = await apiService.getAttendanceCorrectionRequests('pending');
      setCorrectionRequests(res?.success && Array.isArray(res.requests) ? res.requests : []);
    } catch (error) {
      logger.error('Failed to fetch attendance correction requests', { error });
      setCorrectionRequests([]);
    }
  }, []);

  useEffect(() => {
    fetchCorrectionRequests();
  }, [fetchCorrectionRequests]);

  const handleReviewCorrection = async (requestId, action) => {
    if (reviewingCorrectionId) return;
    setReviewingCorrectionId(requestId);
    try {
      const res = await apiService.reviewAttendanceCorrection(requestId, action);
      if (res?.success) {
        setNotification({
          isOpen: true,
          type: 'success',
          title: action === 'approve' ? 'Correction approved' : 'Correction denied',
          message: action === 'approve' ? 'The student\'s attendance has been updated.' : 'The correction request has been denied.',
        });
        setCorrectionRequests((prev) => prev.filter((request) => request.id !== requestId));
      } else {
        setNotification({ isOpen: true, type: 'error', title: 'Action failed', message: res?.message || 'Could not process the correction request.' });
      }
    } catch (error) {
      logger.error('Failed to review attendance correction request', { error });
      setNotification({ isOpen: true, type: 'error', title: 'Action failed', message: error?.message || 'Could not process the correction request.' });
    } finally {
      setReviewingCorrectionId(null);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Attendance Correction Requests</Title>
        <Subtitle>Review student attendance disputes. Auto-approval limits are configured per course in Edit Course.</Subtitle>
      </Header>

      <Section>
        <SectionTitle>Correction Requests{correctionRequests.length > 0 ? ` (${correctionRequests.length})` : ''}</SectionTitle>

        {correctionRequests.length === 0 ? (
          <EmptyRow>No correction requests are awaiting review.</EmptyRow>
        ) : (
          <RequestList>
            {correctionRequests.map((req) => {
              const studentName = [req.first_name, req.last_name].filter(Boolean).join(' ') || req.email || `Student #${req.student_id}`;
              const className = req.course_name || req.elective_name || 'Class';
              const classCode = req.course_code || req.elective_code || '';
              const sessionLabel = req.session_title || 'Session';
              const sessionDate = req.session_date ? formatDateForDisplay(req.session_date, 'MMM d, yyyy') : '';
              const isReviewing = reviewingCorrectionId === req.id;

              return (
                <RequestCard key={req.id}>
                  <RequestMeta>
                    <RequestTitle>
                      {studentName}
                      <ChangePill>{req.current_status} → {req.requested_status}</ChangePill>
                    </RequestTitle>
                    <RequestDetail>
                      {className}{classCode ? ` (${classCode})` : ''} · {sessionLabel}{sessionDate ? ` · ${sessionDate}` : ''}
                    </RequestDetail>
                    {req.reason ? <Reason>“{req.reason}”</Reason> : null}
                  </RequestMeta>
                  <Actions>
                    <ApproveButton
                      type="button"
                      onClick={() => handleReviewCorrection(req.id, 'approve')}
                      disabled={isReviewing}
                    >
                      {isReviewing ? 'Working...' : 'Approve'}
                    </ApproveButton>
                    <DenyButton
                      type="button"
                      onClick={() => handleReviewCorrection(req.id, 'deny')}
                      disabled={isReviewing}
                    >
                      Deny
                    </DenyButton>
                  </Actions>
                </RequestCard>
              );
            })}
          </RequestList>
        )}
      </Section>

      <Notification
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification((prev) => ({ ...prev, isOpen: false }))}
      />
    </Container>
  );
}

export default AdminAttendanceCorrections;
